import React, { useEffect, useState } from "react";
import { matchesMember } from "../lib/memberSearch";
import { SignalPulse } from "./SignalPulse";
import { Loader2, Trash2, Plus, Search, X, Sparkles, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "./Media";

const PERM_LABELS = {
  applications: "Applications",
  requests: "Requests",
  watch: "The Watch Inbox",
  beacon: "Beacons",
  stream: "Stream",
  tips: "Tips",
  growth: "Growth",
  invites: "Signal Keys",
  creed: "The Creed",
  support: "Support",
  quiet: "The Quiet",
  backup: "Backup",
  refresh: "Refresh",
};

export const CouncilManager = () => {
  const [roles, setRoles] = useState({});
  const [perms, setPerms] = useState([]);
  const [council, setCouncil] = useState(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [allCandidates, setAllCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const [{ data: r }, { data: c }] = await Promise.all([
      api.get("/admin/council/roles"),
      api.get("/admin/council"),
    ]);
    setRoles(r.roles); setPerms(r.perms); setCouncil(c);
  };
  useEffect(() => { load(); }, []);

  // Names are E2E-encrypted, so fetch all candidates once and filter locally on
  // the decrypted name (the axios interceptor fills `.name` from `name_enc`).
  const loadCandidates = async () => {
    setSearching(true);
    try { const { data } = await api.get("/admin/council/candidates"); setAllCandidates(data); }
    finally { setSearching(false); }
  };
  useEffect(() => { if (adding) loadCandidates(); /* eslint-disable-next-line */ }, [adding]);

  const candidates = allCandidates.filter((c) => matchesMember(c, q));

  const addMember = async (id) => {
    setBusyId(id);
    try { await api.post("/admin/council", { member_id: id, council_role: null, council_perms: [] }); await load(); setAdding(false); setQ(""); }
    finally { setBusyId(null); }
  };

  const setRole = async (m, role) => {
    setCouncil((arr) => arr.map((x) => x.id === m.id ? { ...x, council_role: role, council_role_label: roles[role] || "" } : x));
    await api.patch(`/admin/council/${m.id}`, { council_role: role || null });
  };

  const togglePerm = async (m, perm) => {
    const next = (m.council_perms || []).includes(perm)
      ? m.council_perms.filter((p) => p !== perm)
      : [...(m.council_perms || []), perm];
    setCouncil((arr) => arr.map((x) => x.id === m.id ? { ...x, council_perms: next } : x));
    await api.patch(`/admin/council/${m.id}`, { council_perms: next });
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the Council? They'll return to being a regular member.`)) return;
    setBusyId(m.id);
    try { await api.delete(`/admin/council/${m.id}`); await load(); }
    finally { setBusyId(null); }
  };

  if (council === null) return <div className="flex justify-center py-16"><SignalPulse size={44} /></div>;

  return (
    <div data-testid="council-manager">
      <p className="font-body text-sm text-zinc-400 mb-6 leading-relaxed">
        Place trusted sovereigns into the Council and hold their keys. Choose a title and grant exactly which parts of this page each steward may access. Only you can change the Council.
      </p>

      <div className="space-y-4">
        {council.map((m) => (
          <div key={m.id} className="border border-white/10 p-4" data-testid={`council-member-${m.id}`}>
            <div className="flex items-center gap-3">
              <Avatar name={m.name} size={40} />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm text-white truncate flex items-center gap-2">
                  {m.name}
                  {m.is_founder && <Sparkles size={13} className="text-[#14A89A]" />}
                </div>
                <div className="font-body text-xs text-zinc-500 truncate">{m.council_role_label || "No title yet"}</div>
              </div>
              {!m.is_founder && (
                <button onClick={() => remove(m)} disabled={busyId === m.id} data-testid={`council-remove-${m.id}`} className="text-zinc-600 hover:text-red-500 transition-colors shrink-0">
                  {busyId === m.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              )}
            </div>

            {m.is_founder ? (
              <div className="mt-3 flex items-center gap-2 text-[#14A89A] font-body text-xs"><ShieldCheck size={14} /> Full access to everything.</div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Title</label>
                  <select value={m.council_role || ""} onChange={(e) => setRole(m, e.target.value)} data-testid={`council-role-${m.id}`} className="w-full bg-[#0F0F0F] border border-white/15 text-white text-sm px-3 py-2.5 font-body focus:outline-none focus:border-[#14A89A]">
                    <option value="">No title</option>
                    {Object.entries(roles).filter(([k]) => k !== "founder").map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Council access</label>
                  <div className="flex flex-wrap gap-2">
                    {perms.map((p) => {
                      const on = (m.council_perms || []).includes(p);
                      return (
                        <button key={p} onClick={() => togglePerm(m, p)} data-testid={`council-perm-${m.id}-${p}`} className={`font-body text-xs px-3 py-1.5 border transition-colors ${on ? "bg-[#14A89A] text-[#0A0A0A] border-[#14A89A]" : "text-zinc-400 border-white/15 hover:border-white/40"}`}>
                          {PERM_LABELS[p] || p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-6 border border-[#14A89A]/30 p-4" data-testid="council-add-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="font-body text-sm text-white">Add a sovereign to the Council</span>
            <button onClick={() => { setAdding(false); setQ(""); }} className="text-zinc-500 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex items-center gap-2 border border-white/15 px-3 mb-3">
            <Search size={15} className="text-zinc-500 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name..." data-testid="council-candidate-search" className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none font-body" />
          </div>
          {searching ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#14A89A]" size={18} /></div>
          ) : candidates.length === 0 ? (
            <p className="font-body text-sm text-zinc-500 py-4 text-center">No matching members.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {candidates.map((c) => (
                <button key={c.id} onClick={() => addMember(c.id)} disabled={busyId === c.id} data-testid={`council-add-${c.id}`} className="w-full flex items-center gap-3 p-2 hover:bg-white/5 transition-colors text-left">
                  <Avatar name={c.name} size={34} />
                  <span className="font-body text-sm text-white flex-1 truncate">{c.name}</span>
                  {busyId === c.id ? <Loader2 size={15} className="animate-spin text-[#14A89A]" /> : <Plus size={15} className="text-[#14A89A]" />}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="council-add-button" className="mt-6 w-full border border-white/20 text-white font-medium px-6 py-3 hover:bg-white/5 transition-colors flex items-center justify-center gap-2 font-body">
          <Plus size={16} /> Add member to Council
        </button>
      )}
    </div>
  );
};
